import { Device, UpdateDeviceConfigFallAlertRequest } from '@/api';
import { useCallback, useEffect, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useOpenApi } from '@/common/providers/open-api-provider';
import { getFallbackErrorKey, isApiError } from '@/common/util';
import { UIToast } from '@/ui';
import { useDevicesTranslation } from '../hooks';
import { addOrUpdateAllDevicesCache } from '../queries/devices-cache';

type UseUpdateDeviceFallAlertActionProps = {
    deviceId: string;
};

type UseUpdateDeviceFallAlertActionData = {
    updateDeviceFallAlert: (
        data: UpdateDeviceConfigFallAlertRequest
    ) => Promise<Device>;
    loading: boolean;
    error: string;
};

/**
 * Turning fall detection on or off on a device, and setting how sensitive
 * it is.
 *
 * No `isAllowed`/`reason`: the gate is the device settings page's own
 * `canEdit`, which it works out once for every action on the device and passes
 * down to the buttons. Whether a *feature* can be turned on is a separate
 * question, answered by `useDeviceAlertLicenses`.
 */
export function useUpdateDeviceFallAlertAction(
    props: UseUpdateDeviceFallAlertActionProps
): UseUpdateDeviceFallAlertActionData {
    const { deviceId } = props;

    const { t } = useDevicesTranslation();
    // The client is swapped out on token refresh, so never capture it: read
    // `openApiRef.current` at call time or you keep using a dead token.
    const { openApi } = useOpenApi();
    const openApiRef = useRef(openApi);
    useEffect(() => {
        openApiRef.current = openApi;
    });

    const queryClient = useQueryClient();

    // One place that turns a failure into a message, so the toast and the
    // returned `error` can never disagree. Every error
    // `PATCH /v3/devices/{deviceId}/config/fall-alert` declares gets a branch.
    const getErrorMessage = useCallback(
        (error: unknown): string => {
            if (isApiError(error, 'DEVICE_NOT_FOUND')) {
                return t('shared.errors.device_not_found');
            }
            if (isApiError(error, 'DEVICE_NOT_SETUP')) {
                return t('shared.errors.device_not_setup');
            }
            if (isApiError(error, 'NOT_SUBSCRIBED')) {
                return t('shared.errors.not_subscribed');
            }
            if (isApiError(error, 'NO_MORE_LICENSES')) {
                return t('shared.errors.no_more_licenses');
            }
            if (isApiError(error, 'NO_PERMISSION')) {
                return t('shared.errors.unauthorised');
            }
            if (isApiError(error, 'BAD_REQUEST')) {
                // The server names the offending field; it is more use than
                // anything generic we could write.
                return error.response?.data.message.join(', ') ?? '';
            }

            return t(getFallbackErrorKey(error));
        },
        [t]
    );

    const mutation = useMutation({
        mutationFn: (data: UpdateDeviceConfigFallAlertRequest) =>
            openApiRef.current.devices
                .updateDeviceConfigFallAlert(deviceId, data)
                .then((response) => response.data.data.device),
        onSuccess: (device) => {
            // The device downloads the new configuration in its own time, so
            // what comes back carries the change as pending. Write it: the
            // lists have to show that it is on its way.
            addOrUpdateAllDevicesCache(queryClient, device);

            UIToast.success(t('actions.update_fall_alert.success'));
        },
        onError: (error) => {
            UIToast.error(getErrorMessage(error));
        }
    });

    return {
        updateDeviceFallAlert: (data) => mutation.mutateAsync(data),
        loading: mutation.isPending,
        error: mutation.error ? getErrorMessage(mutation.error) : ''
    };
}
